/**
 * vee-validate ↔ Zod bridge.
 *
 * Wraps a Zod object schema in vee-validate's `TypedSchema` contract so the
 * form islands can validate with Zod while letting vee-validate drive field
 * state and error display. A minimal in-house adapter is used instead of
 * `@vee-validate/zod` to stay on the project's Zod v4 import and avoid the
 * extra dependency.
 *
 * @see {@link ./buildSchema.ts} - Produces the schema passed to this adapter
 * @module form/zodAdapter
 */
import { z } from "zod";
import type { TypedSchema, TypedSchemaError } from "vee-validate";

/**
 * Adapts a Zod object schema to vee-validate's `TypedSchema` interface.
 *
 * Validation runs asynchronously via `safeParseAsync`. On failure, Zod's
 * flattened field errors are remapped to vee-validate's per-path error shape
 * so messages surface on the matching `<Field>`.
 *
 * @param schema - The Zod object schema to validate form values against
 * @returns A `TypedSchema` consumable by vee-validate's `useForm`
 */
export function toZodTypedSchema(schema: z.ZodObject<z.ZodRawShape>): TypedSchema {
  return {
    __type: "VVTypedSchema",
    async parse(values: unknown) {
      const result = await schema.safeParseAsync(values);
      if (result.success) {
        return { value: result.data as Record<string, unknown>, errors: [] };
      }
      const { fieldErrors } = z.flattenError(result.error);
      const errors: TypedSchemaError[] = Object.entries(fieldErrors).map(([path, messages]) => ({
        path,
        errors: messages ?? [],
      }));
      return { value: undefined, errors };
    },
  };
}
